import React, { memo, useCallback, useState } from 'react';

import { ActionBottomSheet, ModalWrapper } from '$uikit';
import { useTranslator, useUserInfo } from '$hooks';
import { useDispatch, useSelector } from 'react-redux';
import { liveActions, liveActionsSheetUserIdSelector, liveSelector } from '$store/live';
import { IcBan28, IcBanOff28, IcBubble24, IcInfo28, IcUser28 } from '$assets';
import { UnitName } from '$shared/components';
import { UserInfo } from '$core';
import {
  blacklistActions,
  blacklistHasBanFromMeSelector,
  blacklistSelector,
} from '$store/blacklist';
import { RootState } from '$store/rootReducer';

export const LiveUserActionsSheetComponent = () => {
  const t = useTranslator();
  const userId = useSelector(liveActionsSheetUserIdSelector);
  const dispatch = useDispatch();
  const [userInfoId, setUserInfoId] = useState<number | null>(null);
  const user = useUserInfo(userId || 0);
  const banned = useSelector((root: RootState) =>
    blacklistHasBanFromMeSelector(root, userId || 0),
  );

  const handleChange = useCallback(
    (action: string) => {
      switch (action) {
        case 'message':
          window.open(`${origin}/im${user.id}`, '_blank');
          break;
        case 'profile':
          window.open(`${origin}/${user.domain || 'id' + user.id}`, '_blank');
          break;
        case 'info':
          dispatch(liveActions.setOnlineUsersSheet(false));
          dispatch(liveActions.setTotalTipsSheet(false));
          setUserInfoId(user.id);
          break;
        case 'ban':
          dispatch(blacklistActions.ban(user.id));
          break;
        case 'unban':
          dispatch(blacklistActions.unban(user.id));
          break;
        default:
          break;
      }
      dispatch(liveActions.hideActionsSheet());
    },
    [user],
  );

  return (
    <>
      {userInfoId && (
        <ModalWrapper name="user_info" onOverlayClick={() => setUserInfoId(null)}>
          <UserInfo userId={userInfoId} onClose={() => setUserInfoId(null)} />
        </ModalWrapper>
      )}
      <ActionBottomSheet
        options={[
          {
            value: 'message',
            groupId: 'user',
            label: t('live_action_sheet_send_message'),
            icon: <IcBubble24 />,
          },
          {
            value: 'profile',
            groupId: 'user',
            label: t('live_action_sheet_view_profile'),
            icon: <IcUser28 />,
          },
          {
            value: 'info',
            groupId: 'user',
            label: t('chat_user_info'),
            icon: <IcInfo28 />,
          },
          !banned && {
            value: 'ban',
            groupId: 'ban',
            label: t('chat_block'),
            mode: 'alert',
            icon: <IcBan28 />,
          },
          banned && {
            value: 'unban',
            groupId: 'ban',
            label: t('chat_unblock'),
            icon: <IcBanOff28 />,
          },
        ].filter(Boolean)}
        title={<UnitName unit={user} />}
        onChange={handleChange}
        onClose={() => dispatch(liveActions.hideActionsSheet())}
        isOpened={!!user}
      />
    </>
  );
};

export const LiveUserActionsSheet = memo(LiveUserActionsSheetComponent);
